import SiteLayout from "./components/layout/SiteLayout";

function TermsOfService() {
  return (
    <SiteLayout navVariant="inner" footerVariant="legal">
      <main className="pt-32 pb-20 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold text-stone-800 mb-2">Terms of Service</h1>
          <p className="text-stone-500 mb-12">Last updated: January 2026</p>

          <div className="prose prose-stone max-w-none prose-headings:font-bold prose-a:text-brand-orange prose-a:no-underline hover:prose-a:text-brand-orange-hover">
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">1. Acceptance of Terms</h2>
              <p className="text-stone-600 leading-relaxed">
                By installing or using Kyubot ("the Service"), provided by Ninefold ("we", "us"),
                you agree to be bound by these Terms of Service. If you are using the Service on
                behalf of an organization, you represent that you have the authority to bind that
                organization to these terms.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">2. Description of Service</h2>
              <p className="text-stone-600 leading-relaxed">
                Kyubot is a Slack application that provides queue management functionality within
                Slack channels. Users can add items to queues, view queue status, and mark items as
                complete using commands and emoji reactions.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">3. Account & Access</h2>
              <p className="text-stone-600 leading-relaxed mb-4">To use Kyubot, you must:</p>
              <ul className="list-disc pl-6 text-stone-600 space-y-2">
                <li>Have a valid Slack workspace</li>
                <li>Have permission to install apps to your workspace</li>
                <li>Authorize the required Slack permissions for Kyubot to function</li>
              </ul>
              <p className="text-stone-600 leading-relaxed mt-4">
                You are responsible for maintaining the security of your Slack workspace and for all
                activities that occur under your workspace's use of the Service.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">4. Free and Paid Plans</h2>
              <p className="text-stone-600 leading-relaxed mb-4">
                Kyubot offers both free and paid subscription plans:
              </p>
              <ul className="list-disc pl-6 text-stone-600 space-y-2">
                <li>
                  <strong>Free Plan:</strong> Limited to 1 queue and 10 items per queue
                </li>
                <li>
                  <strong>Paid Plan:</strong> Expanded limits, additional features, and priority
                  support
                </li>
              </ul>
              <p className="text-stone-600 leading-relaxed mt-4">
                Paid subscriptions are billed monthly or annually through Stripe. You may cancel
                your subscription at any time; access to paid features will continue until the end
                of your current billing period.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">5. Acceptable Use</h2>
              <p className="text-stone-600 leading-relaxed mb-4">You agree not to:</p>
              <ul className="list-disc pl-6 text-stone-600 space-y-2">
                <li>Use the Service for any unlawful purpose</li>
                <li>Attempt to gain unauthorized access to the Service or its systems</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Use the Service to transmit malware or malicious content</li>
                <li>Resell or redistribute the Service without authorization</li>
                <li>Use automated scripts to excessively load or abuse the Service</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">6. Intellectual Property</h2>
              <p className="text-stone-600 leading-relaxed">
                The Service, including its code, design, and documentation, is owned by Ninefold and
                protected by intellectual property laws. You retain ownership of any content you
                submit through the Service (such as queue item text). By using the Service, you
                grant us a limited license to store and display your content as necessary to operate
                the Service.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">7. Publicity</h2>
              <p className="text-stone-600 leading-relaxed mb-4">
                By using the Service, you grant Ninefold permission to use your organization's name
                and logo on our website, marketing materials, and customer lists to identify you as
                a customer of Kyubot. This permission:
              </p>
              <ul className="list-disc pl-6 text-stone-600 space-y-2">
                <li>Is limited to identifying your organization as a Kyubot customer</li>
                <li>Does not imply endorsement or testimonial without separate written consent</li>
                <li>
                  May be revoked at any time by contacting us at{" "}
                  <a href="mailto:support@kyubot.com" className="text-brand-orange hover:underline">
                    support@kyubot.com
                  </a>
                </li>
              </ul>
              <p className="text-stone-600 leading-relaxed mt-4">
                We will remove your organization's name and logo from our marketing materials within
                30 days of receiving your opt-out request.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">8. Service Availability</h2>
              <p className="text-stone-600 leading-relaxed">
                We strive to maintain high availability but do not guarantee uninterrupted access to
                the Service. The Service may be temporarily unavailable due to maintenance, updates,
                or circumstances beyond our control. We are not liable for any loss or damage
                resulting from service interruptions.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">9. Limitation of Liability</h2>
              <p className="text-stone-600 leading-relaxed mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW:
              </p>
              <ul className="list-disc pl-6 text-stone-600 space-y-2">
                <li>
                  The Service is provided "as is" without warranties of any kind, express or implied
                </li>
                <li>
                  We are not liable for any indirect, incidental, special, consequential, or
                  punitive damages
                </li>
                <li>
                  Our total liability shall not exceed the amount you paid for the Service in the 12
                  months preceding the claim
                </li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">10. Indemnification</h2>
              <p className="text-stone-600 leading-relaxed">
                You agree to indemnify and hold harmless Ninefold and its operators from any claims,
                damages, or expenses arising from your use of the Service or violation of these
                terms.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">11. Termination</h2>
              <p className="text-stone-600 leading-relaxed mb-4">
                We may suspend or terminate your access to the Service at any time for:
              </p>
              <ul className="list-disc pl-6 text-stone-600 space-y-2">
                <li>Violation of these terms</li>
                <li>Non-payment of fees (for paid plans)</li>
                <li>Conduct that we determine is harmful to other users or the Service</li>
              </ul>
              <p className="text-stone-600 leading-relaxed mt-4">
                You may terminate your use of the Service at any time by uninstalling Kyubot from
                your Slack workspace.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">12. Changes to Terms</h2>
              <p className="text-stone-600 leading-relaxed">
                We may modify these terms at any time. We will notify users of material changes by
                posting a notice on our website or through the Service. Continued use of the Service
                after changes constitutes acceptance of the updated terms.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">13. Governing Law</h2>
              <p className="text-stone-600 leading-relaxed">
                These terms are governed by the laws of the Commonwealth of Virginia, United States,
                without regard to conflict of law principles. Any disputes shall be resolved in the
                courts of Fairfax County, Virginia.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">14. Contact</h2>
              <p className="text-stone-600 leading-relaxed">
                For questions about these terms, contact us at:{" "}
                <a href="mailto:support@kyubot.com" className="text-brand-orange hover:underline">
                  support@kyubot.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>
    </SiteLayout>
  );
}

export default TermsOfService;
